import type { ExperienceDto } from "@tripcart/shared";

import { toSameOriginMediaPath } from "./image-variants";

export type MediaSection = "hero" | "gallery" | "description" | "itinerary";

export interface MediaItem {
  id: string;
  url: string;
  alt: string;
  section: MediaSection;
  sectionLabel: string;
  /** image | video — absent is treated as "image". */
  kind?: "image" | "video";
  // Threaded through from the source ImageDto so the lightbox can paint an
  // instant LQIP placeholder (no blank flash) and reserve aspect-ratio.
  // Absent for images extracted from rich-text HTML (carousels / inline <img>),
  // which only expose a URL + alt.
  blurDataURL?: string;
  width?: number;
  height?: number;
}

// Photo identity = URL pathname, normalised so that the media proxy and the
// CDN deduplicate the same asset. Gallery images arrive as Worker proxy URLs
// (`{storefront}/media/agencies/…`) while carousel blocks in descriptionHtml
// embed the raw CDN URL (`media.ontripcartdev.com/agencies/…`). Stripping the
// `/media` prefix from proxy paths makes both collapse to `/agencies/…`.
function photoKey(url: string): string {
  // The base is what makes a RELATIVE url work. `new URL("/media/x")` throws
  // without one, and the old `catch { return url }` then returned the raw
  // string — so a same-origin `/media/agencies/…` keyed as `/media/agencies/…`
  // while the identical absolute `https://host/media/agencies/…` keyed as
  // `/agencies/…`, prefix stripped. Two spellings of one photo that could never
  // compare equal.
  //
  // That is not hypothetical: `collectExperienceMedia` stores the RELATIVE form
  // (`toSameOriginMediaPath`), while an itinerary tile opens the lightbox with
  // the ABSOLUTE url straight off the DTO. Every lookup missed, `openByUrl` fell
  // back to index 0, and clicking any itinerary photo opened the first image in
  // the gallery instead of the one clicked.
  //
  // The catch is REACHABLE, and removing it was a bug. A base rescues a
  // relative url, but a malformed ABSOLUTE one still throws: `new URL("https://",
  // base)`, `new URL("http://", base)` and `new URL("http://%", base)` are all
  // ERR_INVALID_URL in Node. This runs server-side over operator-pasted HTML —
  // `collectExperienceMedia` keys every `<img>` in `descriptionHtml` and in each
  // itinerary day — so one bad `src` would throw inside a Server Component and
  // drop the whole tour page to its error boundary.
  //
  // Returning the raw string on a throw is a deliberate non-match: a malformed
  // url has no pathname to compare, so it only ever equals another copy of the
  // same broken string. That is correct for identity and cannot resurrect the
  // relative-vs-absolute bug above, which was about two VALID spellings.
  //
  // The base is a throwaway — only `pathname` is read from the result.
  try {
    const p = new URL(url, "http://media.invalid").pathname;
    return p.startsWith("/media/") ? p.slice("/media".length) : p;
  } catch {
    return url;
  }
}

// Minimal HTML-entity decode for attribute values that survive JSX render
// (React serialises `"` inside attribute values as `&quot;`, etc.). Covers
// the five named entities we actually emit from the BlockNote blocks. The
// helper is intentionally not a full HTML parser — it only needs to handle
// what React + sanitize-html let through.
function decodeHtmlEntities(s: string): string {
  return s
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

interface ExtractedImage {
  src: string;
  alt: string;
}

// Pull image URLs out of the `data-tc-images` JSON the imageCarousel block
// writes onto its wrapper div. Tolerant of single- or double-quoted attribute
// values and HTML-entity-encoded JSON inside.
function extractCarouselImages(html: string | undefined): ExtractedImage[] {
  if (!html) return [];
  const out: ExtractedImage[] = [];
  const re = /<div\b[^>]*\bdata-tc-images=(?:"([^"]*)"|'([^']*)')[^>]*>/gi;
  for (let match: RegExpExecArray | null; (match = re.exec(html)) !== null; ) {
    const raw = match[1] ?? match[2];
    if (!raw) continue;
    try {
      const parsed = JSON.parse(decodeHtmlEntities(raw)) as unknown;
      if (!Array.isArray(parsed)) continue;
      for (const item of parsed) {
        if (typeof item !== "object" || item === null) continue;
        const obj = item as { url?: unknown; alt?: unknown };
        if (typeof obj.url !== "string" || obj.url.length === 0) continue;
        out.push({
          src: obj.url,
          alt: typeof obj.alt === "string" ? obj.alt : "",
        });
      }
    } catch {
      // skip malformed JSON
    }
  }
  return out;
}

// Pull every `<img>` tag's `src`/`alt` out of the HTML body. Intentionally
// ignores `<img>` tags nested under a carousel wrapper (the carousel block
// today emits no inner imgs, but older saves may have).
function extractInlineImages(html: string | undefined): ExtractedImage[] {
  if (!html) return [];
  // Strip carousel-wrapper subtrees so we don't double-count their imgs.
  const withoutCarousels = html.replace(
    /<div\b[^>]*\bdata-tc-carousel\b[^>]*>[\s\S]*?<\/div>/gi,
    "",
  );
  const out: ExtractedImage[] = [];
  const re = /<img\b[^>]*>/gi;
  for (
    let match: RegExpExecArray | null;
    (match = re.exec(withoutCarousels)) !== null;
  ) {
    const tag = match[0];
    const srcMatch = /\bsrc=(?:"([^"]+)"|'([^']+)')/i.exec(tag);
    if (!srcMatch) continue;
    const src = srcMatch[1] ?? srcMatch[2];
    if (!src) continue;
    const altMatch = /\balt=(?:"([^"]*)"|'([^']*)')/i.exec(tag);
    const alt = altMatch ? (altMatch[1] ?? altMatch[2] ?? "") : "";
    out.push({
      src: decodeHtmlEntities(src),
      alt: decodeHtmlEntities(alt),
    });
  }
  return out;
}

// Single source of truth for every image surfaced on the experience detail
// page. Aggregates from four places so the modal gallery and the hero bento
// show one unified pool:
//   - heroImage (operator-pinned cover)
//   - gallery[]  (BasicsStep gallery uploader)
//   - descriptionHtml (BlockNote tour description — inline imgs + carousels)
//   - itinerary[].description (per-day BlockNote — inline imgs + carousels)
// New image-bearing fields belong here too.
export function collectExperienceMedia(
  data: ExperienceDto | null,
): MediaItem[] {
  if (!data) return [];

  const seen = new Set<string>();
  const out: MediaItem[] = [];

  const push = (
    img: {
      id?: string;
      url: string;
      alt: string;
      kind?: "image" | "video";
      blurDataURL?: string;
      width?: number;
      height?: number;
    },
    section: MediaSection,
    sectionLabel: string,
  ): void => {
    if (!img.url) return;
    // One normalization point for every media consumer (hero bento, gallery
    // grid, lightbox): same-origin relative URLs → short srcsets + the
    // same-origin `/media` route.
    const url = toSameOriginMediaPath(img.url);
    const key = photoKey(url);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      id: img.id ?? key,
      url,
      alt: img.alt || data.name,
      section,
      sectionLabel,
      kind: img.kind,
      blurDataURL: img.blurDataURL,
      width: img.width,
      height: img.height,
    });
  };

  if (data.heroImage?.url) {
    push(
      {
        id: data.heroImage.id ?? undefined,
        url: data.heroImage.url,
        alt: data.heroImage.alt ?? "",
        kind: data.heroImage.kind,
        blurDataURL: data.heroImage.blurDataURL,
        width: data.heroImage.width,
        height: data.heroImage.height,
      },
      "hero",
      "Hero",
    );
  }

  for (const img of data.gallery ?? []) {
    push(
      {
        id: img.id,
        url: img.url,
        alt: img.alt ?? "",
        kind: img.kind,
        blurDataURL: img.blurDataURL,
        width: img.width,
        height: img.height,
      },
      "gallery",
      "Gallery",
    );
  }

  // Basics description — carousels first so they pre-claim their image keys
  // before any duplicate inline copy of the same photo.
  for (const img of extractCarouselImages(data.descriptionHtml)) {
    push({ url: img.src, alt: img.alt }, "description", "Description");
  }
  for (const img of extractInlineImages(data.descriptionHtml)) {
    push({ url: img.src, alt: img.alt }, "description", "Description");
  }

  // Itinerary days — structured per-day images first (operator-curated, the
  // ones the API no longer folds into `gallery`), then any images embedded in
  // the day's rich-text description.
  for (const day of data.itinerary ?? []) {
    for (const img of day.images ?? []) {
      push(
        {
          id: img.id,
          url: img.url,
          alt: img.alt ?? "",
          kind: img.kind,
          blurDataURL: img.blurDataURL,
          width: img.width,
          height: img.height,
        },
        "itinerary",
        "Itinerary",
      );
    }
    const dayHtml = day.description ?? "";
    for (const img of extractCarouselImages(dayHtml)) {
      push({ url: img.src, alt: img.alt }, "itinerary", "Itinerary");
    }
    for (const img of extractInlineImages(dayHtml)) {
      push({ url: img.src, alt: img.alt }, "itinerary", "Itinerary");
    }
  }

  // Lean payload: this array crosses the server→client boundary into the
  // gallery provider, so every field is serialized into the RSC flight
  // stream for EVERY visitor — while its only consumer, the lightbox, is a
  // lazy `ssr:false` chunk that mounts on click and renders `fill` images
  // (no width/height needed). Dropping the base64 `blurDataURL` LQIPs (the
  // heavy part, ~10-20KB per page) costs only the lightbox's cosmetic
  // blur-up; the hero/gallery tiles keep their own blur placeholders via
  // their server-rendered <Image> props.
  return out.map(
    ({ blurDataURL: _blur, width: _w, height: _h, ...lean }) => lean,
  );
}

export function findMediaIndexByUrl(media: MediaItem[], url: string): number {
  if (!url) return -1;
  const target = photoKey(url);
  return media.findIndex((m) => photoKey(m.url) === target);
}
